<script lang='ts'>
import { getSchedule } from './services';
import OpenChatRoom from './OpenChatRoom.svelte';
import { markdown } from './util';
import { ChatEnabled } from './theme';
import { getChannel, sendBird } from './store';
import { onDestroy } from 'svelte';

export let id;
let loading = true;
let show;
let error;
let url;
let isPast;
let showChat = false;
let presentQuestionDialog = false;
let channelSubscription;
let question;
let closeQuestionTimer;
let askQuestionChannel;
let name;
enum QuestionState {
  Idle,
  Sending,
  Sent
}
let questionState = QuestionState.Idle;

function getStreamUrl(stream) {
  switch (stream.type) {
    case 'livestream': return `${stream.url}/player?width=640&height=360&enableInfoAndActivity=true&defaultDrawer=&autoPlay=true&mute=false`;
    case 'vimeo': return `https://player.vimeo.com/video/${stream.url}?autoplay=1&loop=1&autopause=0`;
    case 'dacast': return stream.url;
    case 'ibm': return `https://video.ibm.com/embed/recorded/${stream.url}?autoplay=true&showtitle=false`;
  }
}

function cleanup() {
  channelSubscription?.unsubscribe();
  clearInterval(closeQuestionTimer);
}

async function loadShow(id) {
  try {
    loading = true;

    cleanup();
    name = localStorage.getItem('name');
    
    const schedule = await getSchedule();
    show = schedule.find(s => s.id === id);
    if (!show) {
      error = "Show not found";
      return;
    }

    isPast = show.endTime <= new Date();
    if (isPast && !show.stream.replay) {
      error = "Show has ended";
      return;
    }

    url = getStreamUrl(isPast ? show.stream.replay : show.stream.live);
    if (!url) {
      error = "Unknown stream type";
    }

    channelSubscription?.unsubscribe();
    channelSubscription = getChannel("sendbird_open_channel_8736_d441669c0d15548c6a17bd31fa4c21e06dead134").subscribe(channel => askQuestionChannel = channel)
  } catch (e) {
    console.error(e)
    error = "Unable to load specified show";
  } finally {
    loading = false;
  }
}

function closeQuestionDialog() {
  clearTimeout(closeQuestionTimer);
  presentQuestionDialog = false;
  question = ''
  questionState = QuestionState.Idle;
}

function updateSendbirdName() {
  return new Promise(resolve => {
    sendBird.updateCurrentUserInfo(name, '', resolve);
  });
}

async function sendQuestion(e) {
  e.preventDefault();

  const quest = question.trim();
  if (quest.length === 0) return;

  questionState = QuestionState.Sending;

  await updateSendbirdName();

  const params = new sendBird.UserMessageParams();
  params.message = `(${show.title}) ${quest}`;
  params.pushNotificationDeliveryOption = 'suppress';
  askQuestionChannel.sendUserMessage(params, (userMessage, error) => {
    if (!error) {
      question = ''
      questionState = QuestionState.Sent;

      closeQuestionTimer = setTimeout(closeQuestionDialog, 2000)
    } else {
      console.error(error)
      closeQuestionDialog()
    }
  });
}

onDestroy(() => cleanup())

$: loadShow(id)
</script>

<div class="section-header">MAIN STAGE</div>

{#if error}
  <h1 style="color: red">Error: {error}</h1>
{:else}
  <div class="row">
    <div class="sixteen-nine f1">
      {#if show}
        <iframe
          title="show"
          src={url}
          width="100%"
          height="100%"
          frameborder="0"
          scrolling="no"
          allow="autoplay"
          allowfullscreen="true"
          webkitallowfullscreen="true"
          mozallowfullscreen="true"
          oallowfullscreen="true"
          msallowfullscreen="true"
          class="content" />

        {#if ChatEnabled && !showChat}
          <div class="open-chat-button col vcenter hcenter" on:click={() => showChat = true}>
            <span class="material-icons">chat</span>
          </div>
        {/if}
    {/if}
    </div>
    {#if ChatEnabled && showChat}
      <div class="chatRoomBox col">
        <div class="topbar row vcenter between">
          <div class="chat-button row vcenter hcenter" on:click={() => showChat = true}>
            <span class="material-icons" on:click={() => showChat = false}>close</span>
          </div>

          Live Chat

          <div style="width: 42px" />
        </div>
        <OpenChatRoom url="sendbird_open_channel_8736_b1064a5143d0624c9c126e3378a9108158e98f1f" />
      </div>
    {/if}
  </div>

  <div class="title-box row vcenter between">
    <div class="row vcenter f1">
      <!-- <div class="logo-box logo-ext">
        <div class="logo" />
      </div> -->
      <!-- <div style="white-space: nowrap">2021 NAZ LPC</div> -->
      <!-- <div class="stage-text">{show.title}</div> -->
      <div class='showname'>1H23 Global Brand Conference</div>
    </div>

    {#if show && !isPast}
      <div class="col hcenter">
        <div class='control-button col vcenter hcenter' on:click={() => presentQuestionDialog = true}>
          <img src="/img/icon-bullhorn.svg" alt="Ask a Question" />
        </div>
        <div class="control-label">Ask a Question</div>
      </div>
    {/if}
  </div>

  {#if show}
    <div class="about-box col">
      <div class="title">ABOUT</div>
      <div class="sub-title">{show.title}</div>
      <div class="desc">{@html markdown(show.description)}</div>
    </div>
  {/if}
{/if}

{#if presentQuestionDialog}
  <div class="modal">
    <div class="dialog-container col" class:vcenter={questionState === QuestionState.Sent}>
      {#if questionState !== QuestionState.Sent}
        <div class='row between vcenter mb-8'>
          <div class="dialog-title">Ask a Question</div>
          <div class="control-button col vcenter hcenter" on:click={closeQuestionDialog}><i class="material-icons">close</i></div>
        </div>
        <div class="dialog-label">Your Name</div>
        <input style="margin-bottom: 8px" type='text' bind:value={name} on:change={e => localStorage.setItem('name', e.target.value)} />
        <div class="dialog-label">Question:</div>
        <textarea class="question-text mb-8" bind:value={question} disabled={questionState === QuestionState.Sending} />
        {#if questionState === QuestionState.Sending}
          ...
        {:else}
          <div class="button btn" style="align-self: flex-end" on:click={sendQuestion}>Submit Question</div>
        {/if}
      {:else}
        <div class="question-sent">Sent!</div>
      {/if}
    </div>
  </div>
{/if}

<style lang="scss">
@import './util';
@import './theme';

input, textarea {
  padding: 8px;
  border-radius: 4px;
  border: 1px solid black;
}

.showname {
  white-space: nowrap;
  font-size: 24px;
  font-weight: 600;
  padding: 32px;
}

.chatRoomBox {
  display: flex;
  border: 1px solid rgba(0,0,0,.1);
  width: 280px;
  // padding: 2px;
  // padding-left: 6px;
  background-color: #f7f7f8;

  & > .topbar {
    height: 42px;
    border-bottom: 1px solid rgba(0,0,0,.1);
    background-color: white;
  }
}

$chat-button-size: 42px;

.chat-button {
  cursor: pointer;
  border-radius: 5px;
  width: $chat-button-size;
  height: $chat-button-size;
  &:hover {
    background-color: rgba(255,255,255,.2);
  }
}

.open-chat-button {
  position: absolute;
  top: 4px;
  right: 4px;
  border-radius: 5px;
  width: $chat-button-size;
  height: $chat-button-size;
  color: white;
  z-index: 1;
  cursor: pointer;

  &:hover {
    background-color: rgba(255,255,255,.2);
  }
}

.title-box {
  background-color: var(--gray1);
  font-size: 32px;
  padding-right: 32px;
  color: var(---gray5);
  flex-shrink: 0;
}

.logo-ext {
  padding-left: 50px;
  margin-left: -10px;
}

.stage-text {
  color: var(--gray5);
  font-size: 24px;
  margin-left: 42px;
}

.about-box {
  @include shadow;
  background-color: var(--ice-brew);
  margin: 16px 0;
  padding: 32px 64px;
}

.title {
  color: $primary-color;
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: bold;
}

.sub-title {
  color: var(--grey5);
  margin-bottom: 8px;
  font-weight: bold;
  font-size: 16px;
}

.desc {
  font-size: 16px;
  font-weight: 350;
}

.sixteen-nine {
  @include aspect-ratio(16, 9);
  background-color: black;
}

.dialog-container, .dialog-container-chat {
  width: 400px;
  border-radius: 5px;
  background-color: $cream;
  color: black;
  @include shadow;
  padding: 8px 16px;
}

.dialog-title {
  font-size: 24px;
}

.dialog-label {
  font-size: 16px;
}

.question-text {
  width: 100%;
  height: 80px;
}

.question-sent {
  align-self: center;
  font-size: 26px;
  font-weight: bold;
}

$control-button-size: 42px;

.control-label {
  user-select: none;
  font-size: 14px;
  color: black;
  opacity: .75;
}
.control-button {
  user-select: none;
  cursor: pointer;
  border-radius: 5px;
  width: $control-button-size;
  height: $control-button-size;
  &:hover {
    background-color: rgba(255,255,255,.2);
  }
}
</style>